// Chat interna generale — tutti gli utenti autenticati
import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Send, MessageSquare } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Avatar } from '../../components/ui/Avatar'
import { useAuth } from '../../hooks/useAuth'
import { useRealtime } from '../../hooks/useRealtime'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

interface ChatMessage {
  id: string
  senderId: string
  senderName: string
  senderRole: string
  content: string
  type: string
  createdAt: string
}

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  super_admin: 'Super Admin',
  dispatcher: 'Dispatcher',
  team_leader: 'Team Leader',
  technician: 'Tecnico',
  readonly: 'Readonly',
}

export function ChatPage() {
  const { user } = useAuth()
  const { subscribe } = useRealtime()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data: initialMessages = [], isLoading } = useQuery({
    queryKey: ['internal-chat'],
    queryFn: async () => {
      const res = await api.get<ChatMessage[]>('/chat/messages?limit=100')
      return res.data ?? []
    },
  })

  // Sincronizza i messaggi iniziali dal server
  useEffect(() => {
    if (initialMessages.length > 0) {
      setMessages(initialMessages)
    }
  }, [initialMessages])

  // Messaggi in arrivo via WebSocket
  useEffect(() => {
    const unsub = subscribe('chat:message', (data: unknown) => {
      const msg = data as ChatMessage
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev
        return [...prev, msg]
      })
    })
    return unsub
  }, [subscribe])

  // Scroll automatico in fondo
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await api.post<ChatMessage>('/chat/messages', { content, type: 'text' })
      return res.data
    },
    onSuccess: (msg: ChatMessage) => {
      // Aggiunge subito il messaggio inviato (dedup gestisce eventuale duplicato WS)
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
    },
  })

  const handleSend = () => {
    const text = input.trim()
    if (!text || sendMutation.isPending) return
    setInput('')
    sendMutation.mutate(text)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <PageHeader
          title="Chat interna"
          breadcrumb={[{ label: 'Chat' }]}
        />
      </div>

      {/* Area messaggi */}
      <div
        className="flex-1 overflow-y-auto px-4 py-2 space-y-4"
        role="log"
        aria-live="polite"
        aria-label="Chat generale"
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-sm text-[var(--fc-foreground-muted)]">
            Caricamento messaggi...
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--fc-foreground-muted)]">
            <MessageSquare className="h-10 w-10 opacity-30" />
            <p className="text-sm">Nessun messaggio. Inizia la conversazione!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.senderId === user?.id
            return (
              <div
                key={msg.id}
                className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
              >
                <Avatar name={msg.senderName} size="sm" />
                <div className={`max-w-[70%] flex flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'}`}>
                  {!isOwn && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-[var(--fc-foreground)]">{msg.senderName}</span>
                      <span className="text-[10px] text-[var(--fc-foreground-muted)]">
                        {ROLE_LABEL[msg.senderRole] ?? msg.senderRole}
                      </span>
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 text-sm leading-relaxed ${
                      isOwn
                        ? 'bg-[var(--fc-primary)] text-white rounded-tr-sm'
                        : 'bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground)] rounded-tl-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                  <span className="text-[10px] text-[var(--fc-foreground-muted)] px-1">
                    {formatDate(msg.createdAt, 'HH:mm')}
                  </span>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-[var(--fc-border)] bg-[var(--fc-surface)] px-4 py-3">
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi un messaggio... (Invio per inviare)"
            rows={1}
            className="flex-1 resize-none rounded-xl border border-[var(--fc-border)] bg-[var(--fc-background)] px-4 py-2.5 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] max-h-32"
            aria-label="Messaggio"
            style={{ overflowY: 'auto' }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sendMutation.isPending}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fc-primary)] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--fc-primary-dark)] transition-colors"
            aria-label="Invia messaggio"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
